// The confirm for what cannot be taken back (APS-412): a modal dialog with the consequence, the
// required Motivo (audited) and, for an erasure, the subject typed again; the danger `.btn`
// confirms only once both are there. The Danger zone opens it, and so can a row menu's
// destructive item (RowMenu): one confirm for both.
import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { useI18n } from '../../i18n'
import { ReasonField } from './FormWindow'
import { looksLikeEmail } from './reason'

export function ConfirmDialog({
  open,
  onClose,
  title,
  body,
  confirmLabel,
  dismissLabel,
  typed,
  noEmails,
  busy,
  onConfirm,
}: {
  open: boolean
  /** Esc, Cancelar or a confirm: the opener sets `open` back to false. */
  onClose: () => void
  title: string
  body: ReactNode
  confirmLabel: string
  /** The dismiss button's words, when "Cancelar" would sit by a confirm that cancels too. */
  dismissLabel?: string | undefined
  /** Confirmed only once this is typed again exactly (an org's name, a user hash). */
  typed?: string | undefined
  /** Refuse a Motivo that looks like an email (erasures: the API refuses it too). */
  noEmails?: boolean | undefined
  busy?: boolean | undefined
  onConfirm: (reason: string) => void
}) {
  const { t } = useI18n()
  const ref = useRef<HTMLDialogElement>(null)
  const ids = { title: useId(), typed: useId() }
  const [reason, setReason] = useState('')
  const [text, setText] = useState('')
  const [refusal, setRefusal] = useState('')
  const ready = reason.trim() !== '' && (!typed || text === typed)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  const reset = () => {
    setReason('')
    setText('')
    setRefusal('')
  }
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!ready) return
    if (noEmails && looksLikeEmail(reason)) {
      setRefusal(t('action.reasonNoEmail'))
      return
    }
    const r = reason.trim()
    reset()
    onClose()
    onConfirm(r)
  }
  return (
    <dialog
      ref={ref}
      className="dz-dialog"
      aria-labelledby={ids.title}
      onClose={() => {
        reset()
        onClose()
      }}
    >
      <form className="dz-form" onSubmit={submit}>
        <h2 id={ids.title}>{title}</h2>
        <p>{body}</p>
        <ReasonField
          value={reason}
          onChange={(v) => {
            setReason(v)
            setRefusal('')
          }}
        />
        {typed ? (
          <div className="field-d">
            <label className="lbl" htmlFor={ids.typed}>
              {t('kit.danger.type', { subject: typed })}
            </label>
            <input
              id={ids.typed}
              className="input mono"
              value={text}
              autoComplete="off"
              spellCheck={false}
              translate="no"
              onChange={(e) => setText(e.target.value)}
            />
          </div>
        ) : null}
        {refusal ? (
          <p role="alert" className="field-err">
            {refusal}
          </p>
        ) : null}
        <div className="acts">
          <button type="submit" className="btn danger solid" disabled={!ready || busy}>
            {confirmLabel}
          </button>
          <button type="button" className="btn ghost" onClick={onClose}>
            {dismissLabel ?? t('action.cancel')}
          </button>
        </div>
      </form>
    </dialog>
  )
}
